import { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Receipt, Camera, ScanLine, Fuel, UtensilsCrossed, Smartphone, Package,
  FileSpreadsheet, FileText, FileType, Check, RotateCcw, ArrowRight,
} from "lucide-react";

const SCENES = [
  { key: "problem", ms: 3800 },
  { key: "snap", ms: 4200 },
  { key: "ai", ms: 4600 },
  { key: "savings", ms: 5200 },
  { key: "export", ms: 4200 },
  { key: "cta", ms: 3600 },
];
const TOTAL = SCENES.reduce((s, x) => s + x.ms, 0);

function useCountUp(target, run) {
  const [val, setVal] = useState(0);
  const raf = useRef();
  useEffect(() => {
    if (!run) { setVal(0); return; }
    const startT = performance.now();
    const dur = 2600;
    const tick = (now) => {
      const p = Math.min(1, (now - startT) / dur);
      const eased = 1 - Math.pow(1 - p, 3);
      setVal(target * eased);
      if (p < 1) raf.current = requestAnimationFrame(tick);
    };
    raf.current = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf.current);
  }, [target, run]);
  return val;
}

const spring = { type: "spring", stiffness: 220, damping: 22 };

function Caption({ kicker, children }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 18 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -12 }}
      transition={{ duration: 0.5 }}
      className="text-center max-w-2xl mx-auto"
    >
      <p className="text-xs sm:text-sm uppercase tracking-[0.25em] text-blue-400 font-bold mb-3">{kicker}</p>
      <h2 className="font-heading text-3xl sm:text-5xl font-black tracking-tighter leading-[1.02]">{children}</h2>
    </motion.div>
  );
}

const CATS = [
  { icon: Fuel, vendor: "Shell", amt: "52.40", cat: "Car & truck", tone: "text-blue-300 bg-blue-500/15 border-blue-400/20" },
  { icon: UtensilsCrossed, vendor: "Chipotle", amt: "14.85", cat: "Meals", tone: "text-rose-300 bg-rose-500/15 border-rose-400/20" },
  { icon: Package, vendor: "Staples", amt: "28.99", cat: "Office", tone: "text-amber-300 bg-amber-500/15 border-amber-400/20" },
];

export default function PromoReel() {
  const [i, setI] = useState(0);
  const [playing, setPlaying] = useState(true);
  const t = useRef();

  useEffect(() => {
    if (!playing) return;
    t.current = setTimeout(() => setI((p) => (p + 1) % SCENES.length), SCENES[i].ms);
    return () => clearTimeout(t.current);
  }, [i, playing]);

  const savings = useCountUp(2480, SCENES[i].key === "savings");
  const key = SCENES[i].key;

  const replay = () => { clearTimeout(t.current); setI(0); setPlaying(true); };

  return (
    <div className="fixed inset-0 bg-[#08080c] text-white overflow-hidden grain flex items-center justify-center">
      {/* ambient glows */}
      <div className="absolute -top-40 -right-32 h-[28rem] w-[28rem] rounded-full bg-blue-600/20 blur-[120px]" />
      <div className="absolute -bottom-40 -left-32 h-[28rem] w-[28rem] rounded-full bg-rose-600/15 blur-[120px]" />
      <div className="absolute inset-0 opacity-[0.15]" style={{ backgroundImage: "radial-gradient(circle at 1px 1px, rgba(255,255,255,0.4) 1px, transparent 0)", backgroundSize: "34px 34px" }} />

      {/* stage: 16:9 friendly */}
      <div className="relative w-full max-w-5xl aspect-video px-6 flex flex-col items-center justify-center gap-10">
        <AnimatePresence mode="wait">
          {key === "problem" && (
            <motion.div key="problem" className="flex flex-col items-center gap-10" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
              <div className="relative h-40 w-64">
                {[0, 1, 2, 3, 4].map((n) => (
                  <motion.div key={n}
                    initial={{ opacity: 0, y: -80, rotate: 0 }}
                    animate={{ opacity: 1, y: n * 6, rotate: (n - 2) * 9 }}
                    transition={{ delay: n * 0.12, ...spring }}
                    className="absolute left-1/2 top-4 -translate-x-1/2 w-28 h-36 bg-white/90 rounded-lg shadow-2xl flex flex-col gap-1.5 p-3">
                    <Receipt className="h-4 w-4 text-slate-400" />
                    {[0, 1, 2, 3].map((l) => <div key={l} className="h-1.5 rounded bg-slate-200" style={{ width: `${90 - l * 15}%` }} />)}
                  </motion.div>
                ))}
              </div>
              <Caption kicker="The gig-worker tax nightmare">
                A glovebox full of <span className="text-rose-400">crumpled receipts.</span>
              </Caption>
            </motion.div>
          )}

          {key === "snap" && (
            <motion.div key="snap" className="flex flex-col items-center gap-10" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
              <motion.div initial={{ scale: 0.8, rotate: -4 }} animate={{ scale: 1, rotate: 0 }} transition={spring}
                className="relative w-44 h-56 rounded-2xl bg-white shadow-2xl overflow-hidden p-4">
                <div className="flex items-center gap-1.5 mb-2"><Camera className="h-3.5 w-3.5 text-slate-400" /><span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">Capturing</span></div>
                <p className="text-sm font-black text-slate-800">Shell Gas</p>
                <div className="mt-2 space-y-1.5">{[0,1,2,3].map(l => <div key={l} className="h-2 rounded bg-slate-100" style={{width:`${95-l*12}%`}}/>)}</div>
                <p className="mt-3 text-lg font-black text-slate-900">$52.40</p>
                <motion.div initial={{ top: "-15%" }} animate={{ top: "115%" }} transition={{ duration: 1.2, ease: "linear", repeat: Infinity }}
                  className="absolute left-0 right-0 h-12 bg-gradient-to-b from-transparent via-rose-500/50 to-transparent" />
                <div className="absolute top-3 right-3 flex items-center gap-1 bg-rose-500 text-white text-[9px] font-bold px-2 py-0.5 rounded-full"><ScanLine className="h-3 w-3" /> AI</div>
              </motion.div>
              <Caption kicker="The 2-second fix"><span className="text-blue-400">Just snap it.</span> That's the whole job.</Caption>
            </motion.div>
          )}

          {key === "ai" && (
            <motion.div key="ai" className="flex flex-col items-center gap-10 w-full" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
              <div className="w-full max-w-md space-y-2.5">
                {CATS.map((c, n) => (
                  <motion.div key={n} initial={{ opacity: 0, x: 40 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: n * 0.35, ...spring }}
                    className="flex items-center gap-3 bg-white/5 border border-white/10 rounded-2xl p-3.5 backdrop-blur-sm">
                    <div className="h-10 w-10 rounded-xl bg-white/10 flex items-center justify-center"><c.icon className="h-5 w-5 text-blue-300" /></div>
                    <div className="flex-1"><p className="font-bold text-sm">{c.vendor}</p>
                      <span className={`inline-block mt-0.5 text-[10px] font-bold px-2 py-0.5 rounded-full border ${c.tone}`}>{c.cat}</span></div>
                    <p className="font-heading font-black tabular-nums">${c.amt}</p>
                    <motion.div initial={{ scale: 0 }} animate={{ scale: 1 }} transition={{ delay: n * 0.35 + 0.3, ...spring }}>
                      <Check className="h-5 w-5 text-emerald-400" /></motion.div>
                  </motion.div>
                ))}
              </div>
              <Caption kicker="AI does the boring part">Vendor, amount &amp; <span className="text-blue-400">IRS category</span> — auto-filed.</Caption>
            </motion.div>
          )}

          {key === "savings" && (
            <motion.div key="savings" className="flex flex-col items-center gap-8" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
              <motion.div initial={{ scale: 0.85 }} animate={{ scale: 1 }} transition={spring} className="text-center">
                <p className="text-sm uppercase tracking-[0.25em] text-blue-400 font-bold mb-2">Deductions this year</p>
                <p className="font-heading text-6xl sm:text-8xl font-black tracking-tighter tabular-nums">
                  ${savings.toLocaleString(undefined, { maximumFractionDigits: 0 })}
                </p>
                <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1.6 }}
                  className="mt-3 inline-block text-emerald-300 bg-emerald-500/15 border border-emerald-400/25 rounded-full px-4 py-1.5 text-sm font-bold">
                  ≈ $620 back in your pocket at tax time
                </motion.p>
              </motion.div>
              <Caption kicker="Watch it stack up">Every receipt is <span className="text-emerald-400">money back.</span></Caption>
            </motion.div>
          )}

          {key === "export" && (
            <motion.div key="export" className="flex flex-col items-center gap-10" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
              <div className="flex gap-4">
                {[{ icon: FileText, l: "CSV" }, { icon: FileSpreadsheet, l: "Excel" }, { icon: FileType, l: "PDF" }].map((f, n) => (
                  <motion.div key={n} initial={{ opacity: 0, y: 30, rotate: -6 }} animate={{ opacity: 1, y: 0, rotate: 0 }} transition={{ delay: n * 0.25, ...spring }}
                    className="w-28 h-32 rounded-2xl bg-white/5 border border-white/10 backdrop-blur-sm flex flex-col items-center justify-center gap-3">
                    <f.icon className="h-9 w-9 text-blue-300" />
                    <span className="text-sm font-bold">{f.l}</span>
                  </motion.div>
                ))}
              </div>
              <Caption kicker="Tax time? Sorted.">A CPA-ready report in <span className="text-blue-400">one tap.</span></Caption>
            </motion.div>
          )}

          {key === "cta" && (
            <motion.div key="cta" className="flex flex-col items-center gap-6 text-center" initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0 }} transition={spring}>
              <img src="/brand-icon.png" alt="Receipt Republic" className="h-20 w-20 object-contain drop-shadow-[0_0_30px_rgba(37,99,235,0.6)]" />
              <h2 className="font-heading text-4xl sm:text-6xl font-black tracking-tighter leading-none">
                Tax season,<br /><span className="text-blue-400">done in seconds.</span>
              </h2>
              <div className="inline-flex items-center gap-2 rounded-full bg-blue-600 px-7 py-3.5 text-base font-bold shadow-[0_10px_50px_-8px_rgba(37,99,235,0.8)]">
                Start free at receiptrepublic.com <ArrowRight className="h-4 w-4" />
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* progress bar */}
      <div className="absolute bottom-0 left-0 right-0 h-1 bg-white/10">
        <motion.div key={i} className="h-full bg-blue-500" initial={{ width: "0%" }} animate={{ width: playing ? "100%" : "0%" }} transition={{ duration: SCENES[i].ms / 1000, ease: "linear" }} />
      </div>

      {/* unobtrusive controls (avoid capturing) */}
      <button data-testid="promo-replay" onClick={replay} title="Replay"
        className="absolute top-4 right-4 h-9 w-9 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center text-white/70 transition-colors">
        <RotateCcw className="h-4 w-4" />
      </button>
    </div>
  );
}
